import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { SzIcon, SzTab, SzTypographie } from "@suezenv/react-theme-components";
import { useTranslationWithPrefix } from "../../../hooks/translation";
import { useResponsive } from "../../../hooks/responsive";
import signalService from "../../../services/api/signal-service";
import { IAttachment, SignalRequest } from "../../../type/signal.type";
import SignalComments from "./tabs/comments";
import SignalPictures, { ISignalPicture } from "./tabs/pictures";
import * as mime from 'mime-types';
import { readFileAsBase64 } from "../../../helpers/file.util";

interface ISignalTabs {
    signal: SignalRequest;
    selectedTab: string;
    addComment: () => void;
    addAttachments: () => void;
    changeTab: (selectedTab: string) => void;
}

const ATTACHMENT_DOWNLOAD = '/api/signal/:signalId/attachment/:attachmentId/download'

const SignalTabs: React.FC<ISignalTabs> = ({ signal, selectedTab, addComment, addAttachments, changeTab }) => {
    const { isMobile } = useResponsive();
    const trans = useTranslationWithPrefix("signalement", "detail_signal");
    const [collapsedTab, setCollapsedTab] = useState("");
    const [pictures, setPictures] = useState<ISignalPicture[]>([]);
    const [countAttachments, setCountAttachments] = useState(0);

    const selectTabHandle = (seleted: string): void => {
        changeTab(seleted);
        if (collapsedTab === seleted) {
            setCollapsedTab("");
        } else {
            setCollapsedTab(seleted);
        }
    };

    const handleSignalAttachments = async (): Promise<void> => {
        const { attachments: rawAttachments } = signal;

        if (rawAttachments.length === 0) {
            return;
        }

        setCountAttachments(rawAttachments.length);
        const attachments: ISignalPicture[] = await Promise.all(
            rawAttachments.map(async (attachment: IAttachment) => {
                const link = ATTACHMENT_DOWNLOAD
                    .replace(':signalId', signal.id)
                    .replace(':attachmentId', attachment.file.id);
                const extension = String(mime.extension(attachment.file.mimeType));

                try {
                    // download picture
                    const response: AxiosResponse = await signalService.getAttachment(signal.id, attachment.file.id);
                    const base64data = await readFileAsBase64(response.data);

                    return {
                        data: base64data,
                        fileName: attachment.file.originalFileName,
                        link,
                        extension,
                    };
                } catch {
                    // if file is not a picture
                    return {
                        data: '',
                        fileName: attachment.file.originalFileName,
                        link,
                        extension,
                    };
                }
            })
        );

        setPictures(attachments);
    };

    useEffect((): void => {
        handleSignalAttachments().catch(error => {
            console.error(`Unable to load attachments for the signal ${signal.id}`, error);
        });
    }, [signal.attachments]);

    return (
        <div className={`mb-4 ${selectedTab}`}>
            <SzTab
                activeKey={selectedTab}
                id="signal-detail-tabs"
                className="signal-detail-tabs cursor-pointer mb-3 d-flex"
                selectTabHandle={selectTabHandle}
            >
                <SzTab.SzTabContent
                    eventKey="picture-tab"
                    title={
                        <SzTypographie variant="body">
                            {isMobile && (
                                <SzIcon
                                    variant="line"
                                    icon={`${collapsedTab === "picture-tab" ? "arrow-up-1" : "arrow-down-1"}`}
                                    className="ml-0 mr-2"
                                />
                            )}
                            {trans("attachments")}
                        </SzTypographie>
                    }
                >
                <SignalPictures
                    pictures={pictures}
                    countAttachments={countAttachments}
                    addAttachments={addAttachments}
                />
                </SzTab.SzTabContent>
                <SzTab.SzTabContent
                    eventKey="comment-tab"
                    title={
                        <SzTypographie variant="body">
                            {isMobile && (
                                <SzIcon
                                    variant="line"
                                    icon={`${collapsedTab === "comment-tab" ? "arrow-up-1" : "arrow-down-1"}`}
                                    className="ml-0 mr-2"
                                />
                            )}
                            {trans("comments")}
                        </SzTypographie>
                    }
                >
                    <SignalComments signal={signal} addComment={addComment} />
                </SzTab.SzTabContent>
            </SzTab>
        </div>
    );
};
export default SignalTabs;
